import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { FaArrowLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const SignUpScreen = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [backendMsg, setBackendMsg] = useState("");

  const passwordValue = watch("password");

  const onSubmit = async (data) => {
    setBackendMsg("");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/buyers/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.success) {
        setBackendMsg(result.message);
        localStorage.setItem("signupEmail", data.email); // Store email for confirm screen
        localStorage.removeItem("userToken"); // Clear previous user token
        if (result.token) {
          localStorage.setItem("userToken", result.token); // Store new user token if present
        }
        localStorage.removeItem("cart"); // Clear cart data
        localStorage.removeItem("products"); // Clear products data
        localStorage.removeItem("orders"); // Clear orders data
        setTimeout(() => {
          navigate("/confirm"); // Updated route
        }, 2000);
      } else {
        setBackendMsg(result.message || "An error occurred while creating your account. Please try again later.");
      }
    } catch (err) {
      setBackendMsg("An error occurred while creating your account. Please try again later.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-white">
      <div className="w-full max-w-md px-4 pt-6 pb-24 overflow-y-auto">
        <div className="flex items-start">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#19C463] rounded"
            aria-label="Back to login"
          >
            <FaArrowLeft className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <img
            src="/BUY%20iT%20Logo.png"
            alt="BUY iT logo"
            className="mb-4 h-[64px] w-[53px] object-contain"
          />
          <h1 className="text-center text-xl font-semibold">
            Welcome user! Input details to continue
          </h1>
        </div>

        {/* Backend error/success message at top */}
        {backendMsg && (
          <div className="mb-4 text-center bg-[#FFBABA] text-[#800000] px-4 py-2 rounded-lg font-semibold animate-fadeInOut">
            {backendMsg}
          </div>
        )}
        {/* No verification code message here */}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
          <div>
            <label
              htmlFor="fullName"
              className="block text-left text-sm font-medium text-gray-700"
            >
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              placeholder="Enter your full name"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] sm:text-sm"
              aria-invalid={errors.fullName ? "true" : "false"}
              {...register("fullName", { required: "Full name is required" })}
            />
            {errors.fullName && (
              <p className="mt-1 text-left text-sm text-red-600" role="alert">
                {errors.fullName.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="phoneNumber"
              className="block text-left text-sm font-medium text-gray-700"
            >
              Phone Number
            </label>
            <input
              id="phoneNumber"
              type="tel"
              placeholder="Enter your phone number"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] sm:text-sm"
              aria-invalid={errors.phoneNumber ? "true" : "false"}
              {...register("phoneNumber", {
                required: "Phone number is required",
                pattern: {
                  value: /^[0-9+\-()\s]+$/,
                  message: "Enter a valid phone number",
                },
              })}
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-left text-sm text-red-600" role="alert">
                {errors.phoneNumber.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-left text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] sm:text-sm"
              aria-invalid={errors.email ? "true" : "false"}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter a valid email",
                },
              })}
            />
            {errors.email && (
              <p className="mt-1 text-left text-sm text-red-600" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-left text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="block w-full rounded-md border border-gray-300 pl-3 pr-10 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] sm:text-sm"
                aria-invalid={errors.password ? "true" : "false"}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 z-10"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <FaEyeSlash className="h-4 w-4" />
                ) : (
                  <FaEye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-left text-sm text-red-600" role="alert">
                {errors.password.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-left text-sm font-medium text-gray-700"
            >
              Confirm Password
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                className="block w-full rounded-md border border-gray-300 pl-3 pr-10 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] sm:text-sm"
                aria-invalid={errors.confirmPassword ? "true" : "false"}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === passwordValue || "Passwords do not match",
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 z-10"
                aria-label={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
              >
                {showConfirmPassword ? (
                  <FaEyeSlash className="h-4 w-4" />
                ) : (
                  <FaEye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-left text-sm text-red-600" role="alert">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>
        </form>
      </div>

      <div className="fixed inset-x-0 bottom-0 bg-white p-4">
        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          className="w-full max-w-md mx-auto flex justify-center py-3 px-4 rounded-md bg-[#19C463] text-white text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:ring-offset-2"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default SignUpScreen;
